type Rgb = {
  r: number;
  g: number;
  b: number;
};

export type WcagVersion = "2.1" | "3.0";
export type WcagLevel = "AA" | "AAA";

export const WCAG_LARGE_FONT_THRESHOLD = 18.66;

interface WcagThresholds {
  normalText: number;
  largeText: number;
}

export const WCAG_THRESHOLDS: Record<WcagVersion, Record<WcagLevel, WcagThresholds>> = {
  "2.1": {
    AA: {
      normalText: 4.5,
      largeText: 3.0,
    },
    // These will need to be updated when WCAG 3.0 is finalized
    AAA: {
      normalText: 7.0,
      largeText: 4.5,
    },
  },
  "3.0": {
    AA: {
      normalText: 4.5,
      largeText: 3.0,
    },
    // These will need to be updated when WCAG 3.0 is finalized
    AAA: {
      normalText: 7.0,
      largeText: 4.5,
    },
  },
};

export function luminance(r, g, b) {
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

export function contrastRatio(rgb1: Rgb, rgb2: Rgb) {
  const lum1 = luminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = luminance(rgb2.r, rgb2.g, rgb2.b);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

export function hexToRgb(hex: string): Rgb | null {
  const sanitizedHex = hex.replace(/\##/, "#");
  const colorParts = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(sanitizedHex);

  if (!colorParts) return null;

  const [, r, g, b] = colorParts;

  return {
    r: parseInt(r, 16),
    g: parseInt(g, 16),
    b: parseInt(b, 16),
  } as Rgb;
}

export function hexToRgba(hex: string, opacity: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(0,0,0,1)`;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${opacity})`;
}

// Large text is defined as 18.66px or 14px with a font weight of 700 or higher
export function isWcagLargeText(fontSize: number | undefined, fontWeight: number | undefined): boolean {
  return !!(fontSize && (fontSize >= WCAG_LARGE_FONT_THRESHOLD || (fontSize >= 14 && fontWeight && fontWeight >= 700)));
}

export function checkWcagContrast(
  foregroundHex: string,
  backgroundHex: string,
  options: {
    version?: WcagVersion;
    level?: WcagLevel;
    fontSize?: number;
    fontWeight?: number;
  } = {}
) {
  const {
    version = "2.1", // Default to WCAG 2.1 until WCAG 3.0 is finalized
    level = "AA",
    fontSize,
    fontWeight,
  } = options;

  const fgRgb = hexToRgb(foregroundHex);
  const bgRgb = hexToRgb(backgroundHex);

  if (!fgRgb || !bgRgb) {
    return {
      contrastRatio: "0",
      passes: false,
      details: {
        version,
        level,
        thresholds: WCAG_THRESHOLDS[version][level],
      },
    };
  }

  const ratio = contrastRatio(fgRgb, bgRgb);
  const thresholds = WCAG_THRESHOLDS[version][level];

  const isLargeText = isWcagLargeText(fontSize, fontWeight);

  return {
    contrastRatio: ratio.toFixed(2),
    passes: ratio >= (isLargeText ? thresholds.largeText : thresholds.normalText),
    details: {
      version,
      level,
      isLargeText,
      thresholds,
      fontSize,
      fontWeight,
    },
  };
}

export function isLightColor(hex: string): boolean {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;

  // Compare contrast against both black and white
  const contrastWithBlack = contrastRatio(rgb, { r: 0, g: 0, b: 0 });
  const contrastWithWhite = contrastRatio(rgb, { r: 255, g: 255, b: 255 });

  // If it has better contrast with black, it's a light color
  return contrastWithBlack > contrastWithWhite;
}

export function getContrastColour(hex: string) {
  const isLight = isLightColor(hex);
  if (isLight) return "#000000";
  return "#FFFFFF";
}

export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (c: number) => `0${c.toString(16)}`.slice(-2);
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function lighten(hex: string, intensity: number): string {
  const color = hexToRgb(`#${hex}`);

  if (!color) return "";

  const r = Math.round(color.r + (255 - color.r) * intensity);
  const g = Math.round(color.g + (255 - color.g) * intensity);
  const b = Math.round(color.b + (255 - color.b) * intensity);

  return rgbToHex(r, g, b);
}

export function darken(hex: string, intensity: number) {
  const color = hexToRgb(hex);

  if (!color) return "";

  const r = Math.round(color.r * intensity);
  const g = Math.round(color.g * intensity);
  const b = Math.round(color.b * intensity);

  return rgbToHex(r, g, b);
}
